import React from 'react';
import { Check } from 'lucide-react';

interface Step {
  id: number;
  title: string;
  description: string;
  icon: React.ElementType;
}

interface BudgetStepsProps {
  steps: Step[];
  currentStep: number;
}

export function BudgetSteps({ steps, currentStep }: BudgetStepsProps) {
  return (
    <div className="mb-8 sm:mb-12">
      <div className="md:hidden">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-premium border border-gray-200/50 dark:border-gray-700/50 p-5">
          <div className="flex items-center gap-4">
            <div className={`relative w-14 h-14 rounded-2xl flex items-center justify-center bg-gradient-to-br from-primary-500 to-accent-500 shadow-lg`}>
              {React.createElement(steps[currentStep - 1].icon, {
                className: "w-6 h-6 text-white"
              })}
              <div className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-white dark:bg-gray-800 border-2 border-primary-500 flex items-center justify-center">
                <span className="text-[10px] font-bold text-primary-600 dark:text-primary-400">{currentStep}</span>
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-primary-600 dark:text-primary-400 uppercase tracking-wider">
                Etape {currentStep}/{steps.length}
              </p>
              <h3 className="text-base font-semibold text-gray-900 dark:text-white mt-0.5 truncate">
                {steps[currentStep - 1].title}
              </h3>
            </div>
          </div>
          <div className="mt-4 flex gap-1.5">
            {steps.map((step) => (
              <div
                key={step.id}
                className={`flex-1 h-1.5 rounded-full transition-all duration-500 ${
                  step.id < currentStep
                    ? 'bg-primary-500'
                    : step.id === currentStep
                    ? 'bg-gradient-to-r from-primary-500 to-accent-500'
                    : 'bg-gray-200 dark:bg-gray-700'
                }`}
              />
            ))}
          </div>
        </div>
      </div>

      <div className="hidden md:block">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-premium border border-gray-200/50 dark:border-gray-700/50 p-6 lg:p-8">
          <nav aria-label="Progress">
            <ol className="flex items-start justify-between gap-2">
              {steps.map((step, stepIdx) => {
                const isCompleted = step.id < currentStep;
                const isCurrent = step.id === currentStep;
                const isUpcoming = step.id > currentStep;

                return (
                  <li key={step.id} className="relative flex-1 flex flex-col items-center">
                    {stepIdx !== steps.length - 1 && (
                      <div className="absolute top-6 left-[calc(50%+1.75rem)] w-[calc(100%-3.5rem)] h-0.5" aria-hidden="true">
                        <div className="h-full bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-gradient-to-r from-primary-500 to-accent-500 transition-all duration-700 ease-out rounded-full"
                            style={{ width: isCompleted ? '100%' : '0%' }}
                          />
                        </div>
                      </div>
                    )}

                    <div className="relative flex flex-col items-center group">
                      <div
                        className={`relative w-12 h-12 rounded-2xl flex items-center justify-center transition-all duration-500 ${
                          isCompleted
                            ? 'bg-gradient-to-br from-primary-500 to-primary-600 shadow-lg shadow-primary-500/25 scale-100'
                            : isCurrent
                            ? 'bg-gradient-to-br from-primary-500 to-accent-500 shadow-lg shadow-primary-500/30 ring-[3px] ring-primary-500/20 dark:ring-primary-400/20 scale-110'
                            : 'bg-gray-100 dark:bg-gray-700/50 border-2 border-gray-200 dark:border-gray-600 scale-95'
                        }`}
                      >
                        {isCurrent && (
                          <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-primary-400/30 to-accent-400/30 animate-pulse" />
                        )}
                        {isCompleted ? (
                          <Check className="w-5 h-5 text-white relative z-10" />
                        ) : (
                          React.createElement(step.icon, {
                            className: `w-5 h-5 relative z-10 transition-colors duration-300 ${
                              isCurrent ? 'text-white' : 'text-gray-400 dark:text-gray-500'
                            }`
                          })
                        )}
                      </div>

                      <div className="mt-3 text-center">
                        <span className={`text-sm font-semibold transition-colors duration-300 ${
                          isCompleted || isCurrent
                            ? 'text-gray-900 dark:text-white'
                            : 'text-gray-400 dark:text-gray-500'
                        }`}>
                          {step.title}
                        </span>
                        <p className={`mt-0.5 text-xs transition-colors duration-300 max-w-[10rem] ${
                          isCurrent
                            ? 'text-primary-600 dark:text-primary-400 font-medium'
                            : isCompleted
                            ? 'text-gray-500 dark:text-gray-400'
                            : 'text-gray-400 dark:text-gray-500'
                        }`}>
                          {step.description}
                        </p>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ol>
          </nav>
        </div>
      </div>
    </div>
  );
}